<script setup>
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import camelcaseKeys from 'camelcase-keys';
import CallsAPI from 'dashboard/api/calls';
import { frontendURL } from 'dashboard/helper/URLHelper';
import {
  formatDuration,
  relativeDayTimestamp,
} from 'shared/helpers/timeHelper';
import CallStatusBadge from 'dashboard/components-next/Calls/CallStatusBadge.vue';
import { useCallsStore } from 'dashboard/stores/calls';
import { getCallKind } from 'dashboard/components-next/Calls/constants';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const route = useRoute();
const callsStore = useCallsStore();

// The calls page owns the shared callHistory store; the sidebar keeps its own list so the two
// views never overwrite each other's results.
const calls = ref([]);
const isFetching = ref(false);

const fetchCalls = async () => {
  if (!props.contactId) return;
  isFetching.value = true;
  try {
    const { data } = await CallsAPI.get({ contact_id: props.contactId });
    calls.value = camelcaseKeys(data.payload, { deep: true });
  } catch (error) {
    calls.value = [];
  } finally {
    isFetching.value = false;
  }
};

// A call only lands in a conversation when the contact already had one open. Without one there is
// nothing to open, so the row is not a link.
const conversationLink = call =>
  call.conversation
    ? frontendURL(
        `accounts/${route.params.accountId}/conversations/${call.conversation.displayId}`
      )
    : null;

watch(() => props.contactId, fetchCalls);

// A call in flight is not in this list yet, and the one that just ended is still showing as in
// progress. The live store is what knows: it gains the call when it starts and drops it when it
// ends, so both edges are a reason to ask the server again.
watch(() => callsStore.calls.length, fetchCalls);

onMounted(fetchCalls);
</script>

<template>
  <div class="flex flex-col gap-2 p-2">
    <div v-if="isFetching" class="flex justify-center py-2">
      <Spinner class="size-4 text-n-slate-11" />
    </div>

    <p v-else-if="!calls.length" class="px-2 py-1 text-sm text-n-slate-11">
      {{ $t('CONVERSATION_SIDEBAR.CONTACT_CALLS.EMPTY') }}
    </p>

    <div v-else class="flex flex-col gap-1">
      <component
        :is="conversationLink(call) ? 'router-link' : 'div'"
        v-for="call in calls"
        :key="call.id"
        :to="conversationLink(call)"
        class="flex items-center gap-2 px-2 py-1.5 rounded-lg"
        :class="{ 'hover:bg-n-alpha-1': conversationLink(call) }"
      >
        <span class="shrink-0">
          <CallStatusBadge :kind="getCallKind(call)" />
        </span>
        <span class="text-xs truncate text-n-slate-11">
          {{ relativeDayTimestamp(call.createdAt) }}
        </span>
        <span
          v-if="call.durationSeconds"
          class="text-xs tabular-nums text-n-slate-11 ms-auto shrink-0"
        >
          {{ formatDuration(call.durationSeconds) }}
        </span>
      </component>
    </div>
  </div>
</template>
